// CrossSellStrip — Wave 11
// Renders 3 "frequently paired" catalog cards on product detail pages.
// Pure server-rendered anchors (no JS cost, SEO-indexable). Copy per
// (source × sibling) is hand-tuned and unique — no duplication across
// the site or the workspace.
import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

export type CatalogKey = "naati" | "notary" | "apostille" | "sworn" | "visa";
type Locale = "th" | "en";

type Card = {
  key: CatalogKey;
  hrefTh: string;
  hrefEn: string;
  labelTh: string;
  labelEn: string;
  emoji: string;
  accent: string; // tailwind border/text accent
  rationaleTh: string;
  rationaleEn: string;
};

const CATALOG: Record<CatalogKey, Omit<Card, "rationaleTh" | "rationaleEn">> = {
  naati: {
    key: "naati",
    hrefTh: "/naati/products",
    hrefEn: "/en/naati/products",
    labelTh: "NAATI แปลรับรอง",
    labelEn: "NAATI Certified Translation",
    emoji: "🇦🇺",
    accent: "amber",
  },
  notary: {
    key: "notary",
    hrefTh: "/notary/products",
    hrefEn: "/en/notary/products",
    labelTh: "Notary Public",
    labelEn: "Notary Public",
    emoji: "⚖️",
    accent: "indigo",
  },
  apostille: {
    key: "apostille",
    hrefTh: "/apostille/products",
    hrefEn: "/en/apostille/products",
    labelTh: "Apostille / กงสุล",
    labelEn: "Apostille / MFA Legalisation",
    emoji: "🌐",
    accent: "blue",
  },
  sworn: {
    key: "sworn",
    hrefTh: "/sworn-translator/products",
    hrefEn: "/en/sworn-translator/products",
    labelTh: "Sworn Translator (EU)",
    labelEn: "Sworn Translator (EU)",
    emoji: "📜",
    accent: "purple",
  },
  visa: {
    key: "visa",
    hrefTh: "/visa/products",
    hrefEn: "/en/visa/products",
    labelTh: "วีซ่า & ตม.",
    labelEn: "Visa & Immigration",
    emoji: "🛂",
    accent: "sky",
  },
};

// Curated bundle rationales — unique per (source → sibling) pair.
const PAIRS: Record<CatalogKey, Array<{ key: CatalogKey; rationaleTh: string; rationaleEn: string }>> = {
  naati: [
    { key: "visa",     rationaleTh: "แปล NAATI แล้วยื่นวีซ่าออสเตรเลียต่อเนื่อง — จัดเอกสารครบชุดในสัปดาห์เดียว", rationaleEn: "Pair NAATI translation with Australian visa filing — full document set in one week." },
    { key: "notary",   rationaleTh: "หลายเอกสารต้องผ่าน Notary Public ก่อนแปล NAATI (เช่น หนังสือมอบอำนาจ)", rationaleEn: "Many documents need Notary Public certification before NAATI translation." },
    { key: "apostille",rationaleTh: "หากปลายทางเป็นประเทศสมาชิก Hague ต้องประทับ Apostille เพิ่ม", rationaleEn: "If destination is a Hague-member country, add Apostille after NAATI." },
  ],
  notary: [
    { key: "apostille",rationaleTh: "Notary + Apostille คือคู่พื้นฐานสำหรับใช้เอกสารในต่างประเทศ (Hague)", rationaleEn: "Notary + Apostille is the standard combo for using Thai documents abroad (Hague)." },
    { key: "sworn",    rationaleTh: "ยุโรปหลายประเทศต้องผ่าน Sworn Translator หลังจาก Notary", rationaleEn: "EU countries typically require Sworn Translator certification after Notary." },
    { key: "visa",     rationaleTh: "Notary หนังสือมอบอำนาจ/รับรองสถานภาพ ประกอบการยื่นวีซ่าธุรกิจ", rationaleEn: "Notarise power of attorney/status letters as part of a business visa file." },
  ],
  apostille: [
    { key: "notary",   rationaleTh: "Apostille ต้องผ่าน Notary Public เป็นด่านแรกเสมอ (สำหรับเอกสารเอกชน)", rationaleEn: "Private documents must be Notarised first before Apostille." },
    { key: "sworn",    rationaleTh: "เยอรมนี/ฝรั่งเศส/สเปน มักขอ Sworn Translator หลัง Apostille", rationaleEn: "Germany/France/Spain typically require a Sworn Translator after Apostille." },
    { key: "naati",    rationaleTh: "ออสเตรเลีย/นิวซีแลนด์ ใช้ NAATI แทน Sworn Translator", rationaleEn: "Australia/New Zealand use NAATI in place of a Sworn Translator." },
  ],
  sworn: [
    { key: "apostille",rationaleTh: "Sworn Translator ต้องได้เอกสารที่ผ่าน Apostille แล้วเท่านั้น (เอกสารราชการ)", rationaleEn: "Sworn Translator work is only valid on Apostille-certified originals for public docs." },
    { key: "notary",   rationaleTh: "เอกสารเอกชนต้องผ่าน Notary ก่อน แล้ว Apostille แล้วถึง Sworn Translator", rationaleEn: "Private docs need Notary → Apostille → Sworn Translator in that order." },
    { key: "visa",     rationaleTh: "ยื่นวีซ่ายุโรปประกอบชุดเอกสารครบวงจร", rationaleEn: "Bundle with EU/Schengen visa filing for a complete package." },
  ],
  visa: [
    { key: "notary",   rationaleTh: "หนังสือมอบอำนาจ Notary สำหรับ Non-B / LTR / BOI ยื่นแทนได้", rationaleEn: "Notarised power of attorney lets us file Non-B / LTR / BOI on your behalf." },
    { key: "naati",    rationaleTh: "วีซ่าออสเตรเลีย/นิวซีแลนด์ ต้องใช้เอกสารแปล NAATI", rationaleEn: "Australian/NZ visas require NAATI-certified translations." },
    { key: "sworn",    rationaleTh: "วีซ่า Schengen/German/French ขอ Sworn Translator รับรอง", rationaleEn: "Schengen/German/French visas require Sworn Translator certification." },
  ],
};

const accentClass: Record<string, string> = {
  amber: "border-amber-200 hover:border-amber-400 text-amber-700",
  indigo: "border-indigo-200 hover:border-indigo-400 text-indigo-700",
  blue: "border-blue-200 hover:border-blue-400 text-blue-700",
  purple: "border-purple-200 hover:border-purple-400 text-purple-700",
  sky: "border-sky-200 hover:border-sky-400 text-sky-700",
};

export function CrossSellStrip({ source, locale }: { source: CatalogKey; locale: Locale }) {
  const isTh = locale === "th";
  const pairs = PAIRS[source];
  return (
    <section className="mx-auto mt-12 max-w-6xl px-4">
      <h2 className="font-display text-2xl font-extrabold text-navy-deep">
        {isTh ? "บริการที่ลูกค้ามักซื้อคู่กัน" : "Frequently paired services"}
      </h2>
      <p className="mt-1 text-sm text-slate-600">
        {isTh
          ? "จัดชุดเอกสารครบวงจร ลดเวลาและค่าเดินทาง — คิวรวมทีมเดียว"
          : "Bundle end-to-end document workflows with one coordinated team — fewer trips, faster turnaround."}
      </p>
      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        {pairs.map((p) => {
          const c = CATALOG[p.key];
          const href = isTh ? c.hrefTh : c.hrefEn;
          return (
            <Link
              key={p.key}
              to={href}
              className={`group block rounded-2xl border-2 bg-white p-5 transition hover:-translate-y-0.5 hover:shadow-md ${accentClass[c.accent]}`}
            >
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest">
                <span className="text-lg leading-none">{c.emoji}</span>
                {isTh ? c.labelTh : c.labelEn}
              </div>
              <p className="mt-2 text-sm leading-relaxed text-slate-700">
                {isTh ? p.rationaleTh : p.rationaleEn}
              </p>
              <div className={`mt-3 inline-flex items-center gap-1 text-xs font-semibold ${accentClass[c.accent].split(" ").pop()}`}>
                {isTh ? "ดูแคตตาล็อก" : "View catalog"} <ArrowRight className="h-3 w-3" />
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
